import DiscourseRoute from "discourse/routes/discourse";
import { ajax } from "discourse/lib/ajax";

const FILTERS = ["upcoming", "past", "all"];
const SCOPES = ["all", "my_rsvps", "organizing"];
const VIEWS = ["month", "week", "day", "venue", "agenda"];
const RSVP_STATUSES = ["", "going", "interested", "waitlisted", "not_going"];

function pad(value) {
  return `${value}`.padStart(2, "0");
}

function toDateParam(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseDateParam(value) {
  const [year, month, day] = `${value || ""}`
    .split("-")
    .map((part) => parseInt(part, 10));

  if (!year || !month || !day) {
    return new Date();
  }

  return new Date(year, month - 1, day);
}

function addDays(value, days) {
  const date = value instanceof Date ? new Date(value.getTime()) : parseDateParam(value);
  date.setDate(date.getDate() + days);
  return date;
}

function monthRange(value) {
  const month = parseDateParam(value || toDateParam(new Date()));
  const startsAt = new Date(month.getFullYear(), month.getMonth(), 1);
  const endsAt = new Date(month.getFullYear(), month.getMonth() + 1, 1);

  return {
    start: toDateParam(startsAt),
    end: toDateParam(endsAt),
  };
}

function weekRange(value) {
  const date = parseDateParam(value || toDateParam(new Date()));
  const startsAt = addDays(date, -date.getDay());
  const endsAt = addDays(startsAt, 7);

  return {
    start: toDateParam(startsAt),
    end: toDateParam(endsAt),
  };
}

function dayRange(value) {
  const startsAt = parseDateParam(value || toDateParam(new Date()));
  const endsAt = addDays(startsAt, 1);

  return {
    start: toDateParam(startsAt),
    end: toDateParam(endsAt),
  };
}

function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asBoolean(value) {
  return value === true;
}

// Extract a user-facing message from a failed jQuery/ajax error. Falls back
// to a generic string if the response has no usable error payload.
function extractRouteErrorMessage(error, fallback) {
  const responseJSON = error?.jqXHR?.responseJSON || error?.responseJSON;
  if (Array.isArray(responseJSON?.errors) && responseJSON.errors.length > 0) {
    return responseJSON.errors.join(" ");
  }
  if (typeof responseJSON?.errors === "string") {
    return responseJSON.errors;
  }
  if (responseJSON?.error) {
    return responseJSON.error;
  }
  if (responseJSON?.message) {
    return responseJSON.message;
  }
  return fallback;
}

function extractRouteErrorDetails(error) {
  const responseJSON = error?.jqXHR?.responseJSON || error?.responseJSON || {};

  return {
    type: responseJSON.error_type || "",
    limit: Number(responseJSON.limit) || null,
  };
}

function flattenCategory(category, parentName = "") {
  const label = parentName ? `${parentName} / ${category.name}` : category.name;
  const rows = [
    {
      id: category.id,
      name: label,
      raw_name: category.name,
      slug: category.slug,
      color: category.color,
      text_color: category.text_color,
      parent_category_id: category.parent_category_id || null,
    },
  ];

  asArray(category.subcategory_list).forEach((subcategory) => {
    rows.push(...flattenCategory(subcategory, label));
  });

  return rows;
}

function normalizeCategories(response) {
  const rows = [];
  const seenCategoryIds = new Set();

  asArray(response?.category_list?.categories || response?.categories || response)
    .flatMap((category) => flattenCategory(category))
    .forEach((category) => {
      const id = `${category.id}`;

      if (seenCategoryIds.has(id)) {
        return;
      }

      seenCategoryIds.add(id);
      rows.push(category);
    });

  const categoriesById = new Map(rows.map((category) => [`${category.id}`, category]));

  return rows.map((category) => ({
    ...category,
    name: categoryDisplayName(category, categoriesById),
  }));
}

function categoryBaseName(category) {
  return category?.raw_name || `${category?.name || ""}`.split(" / ").pop();
}

function categoryDisplayName(category, categoriesById, seenCategoryIds = new Set()) {
  const id = `${category?.id || ""}`;

  if (!category?.parent_category_id || seenCategoryIds.has(id)) {
    return categoryBaseName(category);
  }

  seenCategoryIds.add(id);
  const parent = categoriesById.get(`${category.parent_category_id}`);

  if (!parent) {
    return category.name;
  }

  return `${categoryDisplayName(parent, categoriesById, seenCategoryIds)} / ${categoryBaseName(category)}`;
}

function normalizeFilter(filter) {
  return FILTERS.includes(filter) ? filter : "upcoming";
}

function normalizeScope(scope) {
  return SCOPES.includes(scope) ? scope : "all";
}

function normalizeRsvpStatus(status) {
  return RSVP_STATUSES.includes(status) ? status : "";
}

function normalizeView(view) {
  return VIEWS.includes(view) ? view : "month";
}

export default class DiscourseEventsCalendarRoute extends DiscourseRoute {
  queryParams = {
    start: { refreshModel: false },
    end: { refreshModel: false },
    filter: { refreshModel: false },
    q: { refreshModel: false },
    room_id: { refreshModel: false },
    location_id: { refreshModel: false },
    category_id: { refreshModel: false },
    tag_id: { refreshModel: false },
    tag_ids: { refreshModel: false },
    owner_id: { refreshModel: false },
    place_id: { refreshModel: false },
    place_slug: { refreshModel: false },
    place_name: { refreshModel: false },
    event_visibility: { refreshModel: false },
    scope: { refreshModel: false },
    rsvp_status: { refreshModel: false },
    create: { refreshModel: false },
    external_calendars: { refreshModel: false },
    subscribe: { refreshModel: false },
    view: { refreshModel: false },
  };

  async model(params) {
    const today = new Date();
    const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultEnd = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const view = normalizeView(params.view || "agenda");
    const filter = normalizeFilter(params.filter);
    const scope = normalizeScope(params.scope);
    const rsvpStatus = normalizeRsvpStatus(params.rsvp_status);

    let start = params.start || toDateParam(defaultStart);
    let end = params.end || toDateParam(defaultEnd);

    if (view === "week") {
      ({ start, end } = weekRange(start));
    } else if (view === "day" || view === "venue") {
      ({ start, end } = dayRange(start));
    } else if (view === "month" || view === "agenda") {
      ({ start, end } = monthRange(start));
    }

    const eventsRequest = ajax("/calendar/events.json", {
      data: {
        start,
        end,
        summary: true,
        compact_month: view === "month",
        timezone: browserTimeZone(),
        filter,
        q: params.q,
        room_id: params.room_id,
        location_id: params.location_id,
        category_id: params.category_id,
        tag_id: params.tag_id,
        tag_ids: params.tag_ids,
        owner_id: params.owner_id,
        place_id: params.place_id,
        event_visibility: params.event_visibility,
        scope,
        rsvp_status: rsvpStatus,
      },
    }).catch((error) => {
      const details = extractRouteErrorDetails(error);
      if (details.type === "calendar_too_large") {
        return {
          events: [],
          __eventsLoadTooLarge: true,
          __eventsLoadLimit: details.limit,
        };
      }

      // Previously this rejection broke the whole Promise.all and dropped
      // the user onto Discourse's generic error route. Now we degrade
      // gracefully: empty events list with a flag the template can use to
      // surface the failure inline.
      // eslint-disable-next-line no-console
      console.error("[discourse-events-calendar] events fetch failed:", error);
      return {
        events: [],
        __eventsLoadError: extractRouteErrorMessage(error, "Events could not be loaded."),
      };
    });

    const roomsRequest = ajax("/calendar/rooms.json").catch(() => ({ rooms: [] }));
    const locationsRequest = ajax("/calendar/locations.json", {
      data: { summary: true },
    }).catch(() => ({ locations: [] }));
    const categoriesRequest = ajax("/categories.json", {
      data: { include_subcategories: true },
    }).catch(() => ({ categories: [] }));
    const [eventsResponse, roomsResponse, locationsResponse, categoriesResponse] = await Promise.all([
      eventsRequest,
      roomsRequest,
      locationsRequest,
      categoriesRequest,
    ]);

    const events = asArray(eventsResponse?.events || eventsResponse);
    const rooms = asArray(roomsResponse?.rooms || roomsResponse);
    const locations = asArray(locationsResponse?.locations || locationsResponse);
    const categories = normalizeCategories(categoriesResponse);
    const tags = asArray(eventsResponse?.tags);
    const availableTags = asArray(eventsResponse?.available_tags || tags);
    const organizers = asArray(eventsResponse?.organizers);
    const model = Array.isArray(eventsResponse) ? {} : eventsResponse;

    return {
      ...model,
      events,
      eventsLoadError: eventsResponse?.__eventsLoadError || "",
      eventsLoadTooLarge: eventsResponse?.__eventsLoadTooLarge === true,
      eventsLoadLimit: eventsResponse?.__eventsLoadLimit || null,
      rooms,
      locations,
      categories,
      tags,
      availableTags,
      organizers,
      month_overflow_counts: eventsResponse?.month_overflow_counts || {},
      can_create_event: asBoolean(eventsResponse?.can_create_event),
      can_manage_rooms: asBoolean(roomsResponse?.can_manage_rooms),
      filter,
      q: params.q || "",
      room_id: params.room_id || "",
      location_id: params.location_id || "",
      category_id: params.category_id || "",
      tag_id: params.tag_id || "",
      tag_ids: params.tag_ids || "",
      owner_id: params.owner_id || "",
      place_id: params.place_id || "",
      place_slug: params.place_slug || "",
      place_name: params.place_name || "",
      event_visibility: params.event_visibility || "",
      scope,
      rsvp_status: rsvpStatus,
      create: params.create || "",
      external_calendars: params.external_calendars || "",
      subscribe: params.subscribe || "",
      view,
      start,
      end,
    };
  }

  setupController(controller, model) {
    super.setupController(controller, model);
    controller.syncBrowserStateFromModel(model);
  }

  titleToken() {
    return "Calendar";
  }

  // Tear down any document-level listeners the controller may have left
  // active when the user navigates out of the calendar. Without this,
  // every subsequent visit-then-leave-with-popover-open accumulates a
  // dangling listener (popover handlers in the controller add listeners on
  // open and remove them on close — navigation skips the close path).
  resetController(controller, isExiting, transition) {
    super.resetController?.(controller, isExiting, transition);
    if (isExiting && typeof controller.cleanupOpenPopovers === "function") {
      controller.cleanupOpenPopovers();
    }
  }
}
